import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Sheet,
  Table,
  Chip,
  Checkbox,
  Divider,
} from '@mui/joy';
import { Trash as Trash2 } from '@phosphor-icons/react';
import type { RoofFeature } from '../../types';
import { LINE_TYPE_CONFIG, LINE_TYPES, type LineType } from './lineDrawingConstants';

interface LineSegmentsPanelProps {
  features: RoofFeature[];
  visibleTypes: Set<string>;
  onToggleType: (type: string) => void;
  onDeleteFeature: (id: number) => void;
  onHoverFeature?: (id: number | null) => void;
}

export const LineSegmentsPanel: React.FC<LineSegmentsPanelProps> = ({
  features,
  visibleTypes,
  onToggleType,
  onDeleteFeature,
  onHoverFeature,
}) => {
  // Only show line types that exist in the feature list
  const activeTypes = LINE_TYPES.filter((type) =>
    features.some((f) => f.feature_type === type)
  );

  if (features.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography level="title-sm" sx={{ mb: 1 }}>
          Line Segments
        </Typography>
        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
          No lines drawn yet. Use the Draw Lines tool to add ridge, hip, valley,
          and other measurements.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography level="title-sm" sx={{ mb: 1, px: 1 }}>
        Line Segments ({features.length})
      </Typography>

      {/* Show/hide toggles */}
      {activeTypes.length > 1 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, px: 1, mb: 1 }}>
          {activeTypes.map((type) => {
            const config = LINE_TYPE_CONFIG[type];
            const isVisible = visibleTypes.has(type);
            const count = features.filter((f) => f.feature_type === type).length;

            return (
              <Chip
                key={type}
                size="sm"
                variant={isVisible ? 'soft' : 'outlined'}
                sx={{
                  cursor: 'pointer',
                  borderColor: config.color,
                  opacity: isVisible ? 1 : 0.5,
                  '&:hover': { opacity: 1 },
                }}
                startDecorator={
                  <Checkbox
                    size="sm"
                    checked={isVisible}
                    onChange={() => onToggleType(type)}
                    sx={{ mr: -0.5, '--Checkbox-size': '14px' }}
                  />
                }
                onClick={() => onToggleType(type)}
              >
                <Box
                  component="span"
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: config.color,
                    display: 'inline-block',
                    mr: 0.5,
                  }}
                />
                {config.label} ({count})
              </Chip>
            );
          })}
        </Box>
      )}

      <Divider />

      {/* Feature list */}
      <Sheet
        variant="plain"
        sx={{
          maxHeight: 300,
          overflow: 'auto',
        }}
      >
        <Table
          size="sm"
          sx={{
            '& thead th': {
              backgroundColor: 'background.level1',
              fontSize: '10px',
              textTransform: 'uppercase',
              py: 0.5,
            },
            '& tbody td': { py: 0.5, fontSize: '12px' },
            tableLayout: 'fixed',
          }}
        >
          <thead>
            <tr>
              <th style={{ width: '10%' }}></th>
              <th style={{ width: '35%' }}>Type</th>
              <th style={{ width: '35%' }}>Length</th>
              <th style={{ width: '20%' }}></th>
            </tr>
          </thead>
          <tbody>
            {features.map((feature) => {
              const config = LINE_TYPE_CONFIG[feature.feature_type as LineType];
              if (!config) return null;

              return (
                <tr
                  key={feature.id}
                  onMouseEnter={() => onHoverFeature?.(feature.id)}
                  onMouseLeave={() => onHoverFeature?.(null)}
                  style={{
                    cursor: 'pointer',
                    opacity: visibleTypes.has(feature.feature_type) ? 1 : 0.4,
                  }}
                >
                  <td>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: config.color,
                      }}
                    />
                  </td>
                  <td>
                    <Typography level="body-xs" noWrap>
                      {config.label}
                    </Typography>
                  </td>
                  <td>
                    <Typography level="body-xs" sx={{ fontWeight: 'bold' }}>
                      {Number(feature.length_ft).toFixed(1)} ft
                    </Typography>
                  </td>
                  <td>
                    <IconButton
                      size="sm"
                      variant="plain"
                      color="danger"
                      sx={{ '--IconButton-size': '24px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteFeature(feature.id);
                      }}
                    >
                      <Trash2 size={12} />
                    </IconButton>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Sheet>
    </Box>
  );
};
